const { getOffsetTop } = window.BAO.Utils

class ProductMonogrammingDetails extends window.BAO.CustomElement() {
  placementTypes = {
    symbol_only: 'symbol_only',
    text_only: 'text_only',
    text_symbol: 'text_symbol'
  }

  constructor () {
    super()

    this.regex = /^[a-zA-Z0-9\-/.]*$/
  }

  connectedCallback () {
    super.connectedCallback()

    try {
      this.personalisationProps = JSON.parse(this.els.data.element.innerText)
      this.productProps = JSON.parse(this.els['product-data'].element.innerText)
    } catch {
      console.error('Error parsing personalisation props')
      return
    }

    this.configureCanvas()
    this.selectedVariant = this.productProps.variants.find(variant => {
      variant.id === this.els.id.element.value
    })
  }
  
  setupListeners () {
    super.setupListeners()

    this.els.tab.elements.forEach(el => {
      this.listeners.add(el, 'click', this.configureForm.bind(this))
    })

    this.listeners.add(this.els.next.element, 'click', this.generatePreviewImage.bind(this))

    const textInput = this.els['text-input']?.element
    if (textInput) {
      this.listeners.add(textInput, 'keydown', this.onKeyDown.bind(this))
      this.listeners.add(textInput, 'keyup', this.onInputChange.bind(this))
      this.listeners.add(textInput, 'paste', this.onInputPaste.bind(this))
      this.listeners.add(textInput, 'change', this.onInputChange.bind(this))
    }

    this.els.colour.elements.forEach(el => {
      this.listeners.add(el, 'change', this.onColourChange.bind(this))
    })

    this.els.font.elements.forEach(el => {
      this.listeners.add(el, 'change', this.onFontChange.bind(this))
    })

    this.els.symbol.elements.forEach(el => {
      this.listeners.add(el, 'change', this.onSymbolChange.bind(this))
    })

    this.els['symbol-position'].elements.forEach(el => {
      this.listeners.add(el, 'change', this.onSymbolPositionChange.bind(this))
    })

    this.listeners.add(this.els['symbol-trigger'].element, 'click', this.accordionToggleClick.bind(this))

    this.listeners.add(this.els['remove-symbol'].element, 'click', this.removeSymbol.bind(this))

    this.listeners.add(document.getElementById('monogram-drawer'), 'scroll', this.handleScroll.bind(this))

    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PERSONALISATION.RESET,
      this.reset.bind(this),
    )
  }

  configureCanvas () {
    this.personalisationProps.container = this.els['preview'].element

    const canvas = new PreviewCanvas()
    canvas.configure(this.personalisationProps)

    this.personalisationProps.canvas = canvas
  }

  configureForm (e) {
    e.preventDefault()

    const button = e.target.closest('button')
    const placementType = button.dataset.type

    button.closest('.prd-Monogramming_DetailsTabs').classList.add('prd-Monogramming_DetailsTabs-condensed')

    this.els.tab.elements.forEach(el => {
      el.setAttribute('aria-current', el === button)
    })

    const hasSymbol = placementType !== this.placementTypes.text_only
    const hasText = placementType !== this.placementTypes.symbol_only

    // Toggle form elements
    this.els['text-section'].element.setAttribute('aria-hidden', !hasText)
    this.els['font-section'].element.setAttribute('aria-hidden', !hasText)
    this.els['symbol-placement'].element.setAttribute('aria-hidden', !hasText)
    this.els['symbol-section'].element.classList.toggle('prd-Monogramming_DetailsSymbols-expanded', !hasText)
    this.els['symbol-section'].element.setAttribute('aria-hidden', !hasSymbol)

    // Disable form inputs
    this.els['text-input'].element.disabled = !hasText
    this.els.font.elements.forEach(el => { el.disabled = !hasText })
    this.els.symbol.elements.forEach(el => { el.disabled = !hasSymbol })
    this.els['symbol-position'].elements.forEach(el => { el.disabled = !hasSymbol || !hasText })

    this.els.details.element.setAttribute('aria-hidden', false)

    this.setPropertyValue('placementType', placementType, true)
  }

  onKeyDown (e) {
    const { keyCode, key, target: { value } } = e
    
    if (keyCode === 13) {
      e.preventDefault()
      return false
    }

    if (value.length >= this.maxAllowableCharacters && key.length === 1) {
      e.preventDefault()
      return false
    }
  }

  onInputPaste (e) {
    window.setTimeout(() => {
      e.target.dispatchEvent(new Event('change'))
    }, 1)
  }

  onInputChange (e) {
    const { target: { value } } = e

    const textInput = this.els['text-input'].element
    
    if (value.length > this.maxAllowableCharacters) {
      textInput.value = value.substring(0, this.maxAllowableCharacters)
    }
    
    this.setPropertyValue('text', textInput.value, true)
  }

  updateCharCount () {
    this.els['character-count'].element.innerText = this.totalCharacters

    const atCharacterLimit = this.personalisationProps.text.length === this.maxAllowableCharacters
    const hasSymbol = !!this.personalisationProps.symbol
    const isTextOnly = this.personalisationProps.placementType === this.placementTypes.text_only
    const disabled = (atCharacterLimit && !hasSymbol) || isTextOnly

    this.els.symbol.elements.forEach(el => {
      el.disabled = disabled
    })
  }

  setVariantInput () {
    const input = this.els.id?.element

    if (!input) return

    const variants = this.productProps?.variants

    if (!variants) return

    let price = 0

    if (this.totalCharacters === 0) {
      this.selectedVariant = null
      input.value = ''
      price = 0
    } else {
      this.selectedVariant = variants[Math.max(0, this.totalCharacters - 1)]
      input.value = this.selectedVariant.id
      price = this.selectedVariant.price
    }

    const priceEl = this.externalEls.price.element
    priceEl.dataset[`item-${this.index}`] = price
    
    const total = parseInt(priceEl.dataset['item-1']) + parseInt(priceEl.dataset['item-2'])
    priceEl.innerText = `+${window.BAO.Utils.formatMoney(total)}`
  }

  onFontChange (e) {
    const { target: { checked, value} } = e

    if (checked) {
      this.setPropertyValue('font', value, true)
    }
  }

  onColourChange (e) {
    const { target: { checked, value, dataset } } = e

    if (checked) {
      this.setPropertyValue('colour', dataset.colour, true)
    }
  }

  onSymbolChange (e) {
    const { target: { checked, value} } = e

    if (checked) {
      this.setPropertyValue('symbol', value, true)
    }
  }

  onSymbolPositionChange (e) {
    const { target: { checked, value} } = e

    if (checked) {
      this.setPropertyValue('symbolPosition', value, true)
    }
  }

  removeSymbol (e) {
    e.preventDefault()

    this.els.symbol.elements.find(el => el.checked).checked = false

    this.setPropertyValue('symbol', '', true)
  }

  setPropertyValue (property, value, updateCanvas) {
    this.personalisationProps[property] = value

    this.updateCharCount()
    this.setVariantInput()
    this.handleButton()

    if (updateCanvas) {
      this.updatePreview()
    }

    if (property === 'colour') {
      this.els['colour-label'].element.innerText = this.els.colour.elements.find(el => el.dataset.colour === value).value
    }

    if (property === 'placementType' || property === 'symbol') {
      const symbol = window.monogramSymbols[this.personalisationProps.symbol]
      const isSymbolOnly = this.personalisationProps.placementType === this.placementTypes.symbol_only
      const isTextAndSymbol = this.personalisationProps.placementType === this.placementTypes.text_symbol
      const hideColours = symbol?.fixedColour && isSymbolOnly
      
      this.els.colour.elements.forEach(el => { el.disabled = hideColours })

      if (hideColours) {
        this.els['colour-section'].element.setAttribute('hidden', '')
        this.els['step-2'].element.setAttribute('hidden', '')
      } else {
        this.els['colour-section'].element.removeAttribute('hidden')
        this.els['step-2'].element.removeAttribute('hidden')
      }

      this.els['remove-symbol'].element.style.display = isTextAndSymbol && !!symbol ? 'block' : 'none'
    }
  }

  updatePreview () {
    this.personalisationProps.canvas.render(this.personalisationProps)
  }

  async generatePreviewImage () {
    // Generate preview image and set it on summary pages
    const preview = await this.personalisationProps.canvas.render(this.personalisationProps)
    
    this.els[`preview-input`].element.value = preview

    const previewImage = this.externalEls[`item-${this.index}-preview-image`].element
    previewImage.src = preview
    previewImage.style.display = 'block'

    this.externalEls[`item-${this.index}-preview`].element.dataset.active = 'true'
  }

  handleButton () {
    const nextButton = this.els.next.element

    const hasText = this.personalisationProps.text !== ''
    const hasSymbol = this.personalisationProps.symbol !== ''
    const hasColour = this.personalisationProps.colour !== ''
    const hasFont = this.personalisationProps.font !== ''

    let validInputs = false
    let requiresText = false
    let requiresSymbol = false

    switch (this.personalisationProps.placementType) {
      case this.placementTypes.symbol_only:
        validInputs = hasSymbol && hasColour
        requiresSymbol = true
        break
      case this.placementTypes.text_only:
        validInputs = hasText && hasColour && hasFont
        requiresText = true
        break
      case this.placementTypes.text_symbol:
        validInputs = hasText && hasSymbol && hasColour && hasFont
        requiresText = true
        requiresSymbol = true
        break
    }

    this.els['text-error'].element.style.display = 'none'
    if (requiresText && !this.personalisationProps.text.match(this.regex)) {
      validInputs = false
      this.els['text-error'].element.style.display = 'block'
    }

    nextButton.disabled = !validInputs

    if (validInputs) {
      nextButton.innerText = nextButton.dataset.enabledText
    } else if (requiresText && !hasText) {
      nextButton.innerText = nextButton.dataset.disabledText
    } else if (requiresSymbol && !hasSymbol) {
      nextButton.innerText = nextButton.dataset.disabledSymbol
    }
  }

  accordionToggleClick (e) {
    e.preventDefault()

    const expanded = e.target.closest('button').getAttribute('aria-expanded') === 'true'

    this.toggleSymbolAccordion(!expanded)
  }

  toggleSymbolAccordion (open) {
    const trigger = this.els['symbol-trigger'].element
    const symbols = this.els['symbol-container'].element

    trigger.setAttribute('aria-expanded', open)
    
    if (open) {
      symbols.removeAttribute('hidden')
    } else {
      symbols.setAttribute('hidden', '')
    }
  }

  reset () {
    // Reset inputs
    this.els['text-input'].element.value = ''
    this.els.symbol.elements.forEach(el => { el.checked = false })
    this.els['symbol-position'].elements.forEach((el, index) => { el.checked = index === 0 })
    this.els.font.elements.forEach((el, index) => { el.checked = index === 0 })
    this.els.colour.elements.forEach((el, index) => { el.checked = index === 0 })

    // Reset property values
    this.setPropertyValue('placementType', '', false)
    this.setPropertyValue('text', '', false)
    this.setPropertyValue('symbol', '', false)
    this.setPropertyValue('symbolPosition', this.els.font.element.value, false)
    this.setPropertyValue('font', this.els.font.element.value, false)
    this.setPropertyValue('colour', this.els.colour.element.dataset.colour, true)

    // Close symbol accordion
    this.toggleSymbolAccordion(false)
    
    // Reset tabs
    this.els.tab.element.closest('.prd-Monogramming_DetailsTabs').classList.remove('prd-Monogramming_DetailsTabs-condensed')
    this.els.tab.elements.forEach(el => {
      el.setAttribute('aria-current', false)
    })

    // Hide form
    this.els.details.element.setAttribute('aria-hidden', true)

    // Clear preview
    this.els[`preview-input`].element.value = ''
    this.externalEls[`item-${this.index}-preview`].element.dataset.active = 'false'
    this.externalEls[`item-${this.index}-preview-image`].element.src = ''
    this.externalEls[`item-${this.index}-preview-image`].element.style.display = 'none'
  }

  duplicate () {
    const targetIndex = this.index === 1 ? 2 : 1
    const target = document.querySelector(`product-monogramming-details[data-index="${targetIndex}"]`)
    const properties = target.propertyValues

    // Reset inputs
    this.els['text-input'].element.value = properties.text
    this.els.symbol.elements.forEach(el => { el.checked = el.value === properties.symbol })
    this.els['symbol-position'].elements.forEach(el => { el.checked = el.value === properties.symbolPosition })
    this.els.font.elements.forEach(el => { el.checked = el.value === properties.font })
    this.els.colour.elements.forEach(el => { el.checked = el.dataset.colour === properties.colour })

    // Reset property values
    this.setPropertyValue('placementType', properties.placementType, false)
    this.setPropertyValue('text', properties.text, false)
    this.setPropertyValue('symbol', properties.symbol, false)
    this.setPropertyValue('symbolPosition', properties.symbolPosition, false)
    this.setPropertyValue('font', properties.font, false)
    this.setPropertyValue('colour', properties.colour, false)

    this.els.tab.elements.forEach(el => {
      if (el.dataset.type === properties.placementType) el.click()
    })

    this.generatePreviewImage()
  }

  handleScroll (e) {
    if (this.parentElement.getAttribute('aria-current') !== 'true') return

    const initialOffset = 86
    const initialMargin = 30
    const initialHeight = 270

    const previewContainer = this.els['preview-container'].element

    const offsetTop = previewContainer.offsetTop - initialOffset
    const rect = previewContainer.getBoundingClientRect()

    let height = initialHeight

    if (rect.y > 0) {
      height = Math.min(rect.height + rect.y, initialHeight)
    } else {
      const offset = Math.min(Math.max(offsetTop, 0), 100)
      height = initialHeight - offset
    }

    previewContainer.style.height = `${height}px`
    previewContainer.style.marginTop = `${initialHeight - height + 30}px`
    previewContainer.querySelector('div').style.marginTop = `-${(initialHeight - height) / 2}px`
  }

  get propertyValues () {
    return this.personalisationProps
  }

  get index () {
    return parseInt(this.dataset.index)
  }

  get characterCount () {
    if (this.personalisationProps.placementType === this.placementTypes.symbol_only) {
      return 0
    }

    return this.personalisationProps.text.length
  }

  get hasSymbol () {
    if (this.personalisationProps.placementType === this.placementTypes.text_only) {
      return false
    }

    return !!this.personalisationProps.symbol
  }

  get maxAllowableCharacters () {
    const maxLength = this.els['text-input'].element.maxLength
    return maxLength - (this.hasSymbol ? 1 : 0)
  }

  get totalCharacters () {
    return this.characterCount + (this.hasSymbol ? 1 : 0)
  }
}

class PreviewCanvas {
  async configure (props) {
    const width = Math.min(window.innerWidth - 45, 445)

    this.stage = new window.Konva.Stage({
      container: props.container,
      width: width,
      height: width * 0.8,
      draggable: false,
    })

    await this.generateBackground(props)
  }

  async render (props) {
    if (this.textLayer) {
      this.textLayer.remove()
    }

    this.textLayer = new window.Konva.Layer()
    this.stage.add(this.textLayer)

    const group = new window.Konva.Group({
      id: `text-group`,
      x: this.stageWidth / 2,
      y: 0
    })

    const textGroup = new window.Konva.Group()

    let textWidth = 0

    if (props.placementType != 'text_only' && props.symbolPosition != 'After Text') {
      textWidth += await this.generateSymbol(props, textGroup, textWidth)
    }

    if (props.placementType != 'symbol_only') {
      textWidth += await this.generateText(props, textGroup, textWidth)
    }

    if (props.placementType != 'text_only' && props.symbolPosition == 'After Text') {
      textWidth += await this.generateSymbol(props, textGroup, textWidth)
    }

    textGroup.offsetX(textWidth / 2)

    group.add(textGroup)

    this.textLayer.add(group)
    
    return await this.generatePreview()
  }

  async generatePreview () {
    if (!this.stage) return

    const preview = await this.stage.toDataURL({ mimeType: 'image/jpeg' })

    return preview
  }

  async generateBackground (props) {
    const backgroundLayer = new window.Konva.Layer()

    const backgroundImg = await this.createImage(props.backgroundImage)

    const backgroundImage = new window.Konva.Image({
      image: backgroundImg,
      x: 0,
      y: 0,
      width: this.stageWidth,
      height: this.stageHeight,
      draggable: false,
    })

    backgroundLayer.add(backgroundImage)
    this.stage.add(backgroundLayer)
  }

  async generateText (props, group, textWidth) {
    if (!props.text) return 0

    const font = window.monogramFonts[props.font.toLowerCase()]
    const fontSize = this.fontSize * font.scale

    const textGroup = new window.Konva.Group()

    const text = new window.Konva.Text({
      text: props.text,
      fill: props.colour,
      fontFamily: font.fontFamily,
      fontSize: fontSize,
      letterSpacing: fontSize * font.letterSpacing,
      offsetY: fontSize * font.offset,
      x: textWidth,
      y: this.stageHeight / 2,
      name: 'main-text',
    })

    textGroup.add(text)

    const rect = textGroup.getClientRect()

    textGroup.offsetY(rect.height / 2)

    group.add(textGroup)

    return rect.width + this.spacer
  }

  async generateSymbol (props, group, textWidth) {
    if (!props.symbol) return 0

    const symbolData = window.monogramSymbols[props.symbol]

    const scale = (this.fontSize / 200) * symbolData.scale

    const resp = await fetch(symbolData.image);
    const parser = new DOMParser();
    const doc = parser.parseFromString(await resp.text(), 'image/svg+xml');
    const svg = doc.querySelector('svg')
    const svgWidth = parseInt(svg.getAttribute('width'))
    const svgHeight = parseInt(svg.getAttribute('height'))
    const paths = svg.querySelectorAll('path');

    const svgGroup = new window.Konva.Group({
      x: textWidth,
      y: this.stageHeight / 2
    })

    const pathGroup = new window.Konva.Group({
      scaleX: scale,
      scaleY: scale
    })

    paths.forEach(data => {
      const path = new window.Konva.Path({
        fill: symbolData.fixedColour ? data.getAttribute('fill') : props.colour,
        stroke: symbolData.fixedColour ? data.getAttribute('stroke') : props.colour,
        data: data.getAttribute('d'),
        x: 0,
        y: 0
      })

      pathGroup.add(path)
    })

    const rect = pathGroup.getClientRect()
    const pathOffsetX = ((svgWidth * scale) - (rect.width * scale)) / 2
    const pathOffsetY = ((svgHeight * scale) - (rect.height * scale)) / 2

    pathGroup.offsetX(pathOffsetX)
    pathGroup.offsetY(pathOffsetY)
    
    const background = new window.Konva.Rect({
      width: rect.width,
      height: rect.height,
      x: 0,
      y: 0
    })
    svgGroup.add(background)

    svgGroup.add(pathGroup)
    svgGroup.offsetY(rect.height / 2)
    
    group.add(svgGroup)

    return rect.width + this.spacer
  }

  createImage (url) {
    return new Promise((resolve, reject) => {
      const image = new Image()
      image.crossOrigin = ''
      image.addEventListener('load', () => resolve(image))
      image.addEventListener('error', (error) => reject(error))
      image.src = url
    })
  }

  get stageWidth () {
    return this.stage.width()
  }

  get stageHeight () {
    return this.stage.height()
  }

  get fontSize () {
    return this.stageWidth / 4
  }

  get spacer () {
    return this.fontSize / 4
  }
}

if (!customElements.get('product-monogramming-details')) {
  customElements.define('product-monogramming-details', ProductMonogrammingDetails)
}
